import { Bell, CheckSquare, Inbox } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useWorkflowNotifications, useWorkflowTasks } from '@/hooks/useWorkflows';
import type { ApiNotification, ApiTask } from '@/api/workflows';

function formatDate(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function EmptyState({ icon: Icon, message }: { icon: typeof Inbox; message: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-center">
      <Icon className="h-8 w-8 text-muted-foreground/60" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-2 p-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-16 w-full rounded-lg" />
      ))}
    </div>
  );
}

const TASK_STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = {
  open: 'default',
  done: 'secondary',
  closed: 'secondary',
};

export function WorkflowNotificationsList() {
  const { data, isLoading, isError } = useWorkflowNotifications({ limit: 100 });
  const items: ApiNotification[] = data?.data ?? [];

  if (isLoading) return <ListSkeleton />;
  if (isError) return <EmptyState icon={Bell} message="Failed to load notifications." />;
  if (items.length === 0) {
    return <EmptyState icon={Bell} message="No notifications yet." />;
  }

  return (
    <ScrollArea className="h-full">
      <ul className="space-y-2 p-4">
        {items.map((n) => (
          <li
            key={n.id}
            className="rounded-lg border bg-card px-4 py-3 transition-colors hover:border-primary/40"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-foreground">{n.title}</p>
              {!n.is_read && (
                <Badge variant="default" className="shrink-0">
                  New
                </Badge>
              )}
            </div>
            {n.message && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{n.message}</p>
            )}
            <p className="mt-1.5 text-[11px] text-muted-foreground/70">
              {formatDate(n.created_at)}
            </p>
          </li>
        ))}
      </ul>
    </ScrollArea>
  );
}

export function WorkflowTasksList() {
  const { data, isLoading, isError } = useWorkflowTasks({ limit: 100 });
  const items: ApiTask[] = data?.data ?? [];

  if (isLoading) return <ListSkeleton />;
  if (isError) return <EmptyState icon={CheckSquare} message="Failed to load tasks." />;
  if (items.length === 0) {
    return <EmptyState icon={CheckSquare} message="No tasks yet." />;
  }

  return (
    <ScrollArea className="h-full">
      <ul className="space-y-2 p-4">
        {items.map((t) => (
          <li
            key={t.id}
            className="rounded-lg border bg-card px-4 py-3 transition-colors hover:border-primary/40"
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-foreground">{t.title}</p>
              <Badge variant={TASK_STATUS_VARIANT[t.status] ?? 'outline'} className="shrink-0">
                {t.status}
              </Badge>
            </div>
            {t.message && (
              <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{t.message}</p>
            )}
            <p className="mt-1.5 text-[11px] text-muted-foreground/70">
              {formatDate(t.created_at)}
            </p>
          </li>
        ))}
      </ul>
    </ScrollArea>
  );
}
